import { FC, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

interface CanvasBackgroundImageProps {
  imageSrc: string | null;
  containerWidth: number;
  containerHeight: number;
  backgroundSize?: 'contain' | 'cover' | 'fill' | 'scale-down' | '100% 100%';
  backgroundPosition?: 'center' | 'top' | 'bottom' | 'left' | 'right';
  backgroundRepeat?: 'no-repeat' | 'repeat' | 'repeat-x' | 'repeat-y';
  pixelRatio?: number;
  className?: string;
  style?: React.CSSProperties;
}

const CanvasBackgroundImage: FC<CanvasBackgroundImageProps> = ({
  imageSrc,
  containerWidth,
  containerHeight,
  backgroundSize = 'contain',
  backgroundPosition = 'center',
  backgroundRepeat = 'no-repeat',
  pixelRatio = 2,
  className,
  style,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!imageSrc || !canvasRef.current) {
      setImageLoaded(false);
      setImageError(false);
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size with high DPI
    const actualWidth = containerWidth * pixelRatio;
    const actualHeight = containerHeight * pixelRatio;

    canvas.width = actualWidth;
    canvas.height = actualHeight;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;

    // Scale the context to match the pixel ratio
    ctx.scale(pixelRatio, pixelRatio);

    // Load and draw the image
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      setImageError(false);

      // Calculate image dimensions based on backgroundSize
      let drawWidth = containerWidth;
      let drawHeight = containerHeight;
      let drawX = 0;
      let drawY = 0;

      if (backgroundSize === 'contain') {
        const imageAspectRatio = img.width / img.height;
        const containerAspectRatio = containerWidth / containerHeight;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider, fit to width
          drawWidth = containerWidth;
          drawHeight = containerWidth / imageAspectRatio;
        } else {
          // Image is taller, fit to height
          drawHeight = containerHeight;
          drawWidth = containerHeight * imageAspectRatio;
        }
      } else if (backgroundSize === 'cover') {
        const imageAspectRatio = img.width / img.height;
        const containerAspectRatio = containerWidth / containerHeight;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider, fit to height
          drawHeight = containerHeight;
          drawWidth = containerHeight * imageAspectRatio;
        } else {
          // Image is taller, fit to width
          drawWidth = containerWidth;
          drawHeight = containerWidth / imageAspectRatio;
        }
      } else if (backgroundSize === '100% 100%') {
        drawWidth = containerWidth;
        drawHeight = containerHeight;
      }

      // Calculate position based on backgroundPosition
      if (backgroundPosition === 'center') {
        drawX = (containerWidth - drawWidth) / 2;
        drawY = (containerHeight - drawHeight) / 2;
      } else if (backgroundPosition === 'top') {
        drawX = (containerWidth - drawWidth) / 2;
        drawY = 0;
      } else if (backgroundPosition === 'bottom') {
        drawX = (containerWidth - drawWidth) / 2;
        drawY = containerHeight - drawHeight;
      } else if (backgroundPosition === 'left') {
        drawX = 0;
        drawY = (containerHeight - drawHeight) / 2;
      } else if (backgroundPosition === 'right') {
        drawX = containerWidth - drawWidth;
        drawY = (containerHeight - drawHeight) / 2;
      }

      // Clear canvas
      ctx.clearRect(0, 0, containerWidth, containerHeight);

      // Draw the image
      ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

      setImageLoaded(true);
    };

    img.onerror = () => {
      setImageError(true);
      setImageLoaded(false);
    };

    img.src = imageSrc;
  }, [imageSrc, containerWidth, containerHeight, backgroundSize, backgroundPosition, pixelRatio]);

  if (!imageSrc || imageError) {
    return null;
  }

  return (
    <Box
      className={className}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        ...style,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          display: imageLoaded ? 'block' : 'none',
        }}
      />
    </Box>
  );
};

export default CanvasBackgroundImage;
